import React, { useState, useCallback } from 'react'; // 🛑 Import useState and useCallback
import { Typography, Stack, Box } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'; // Removed Legend import
import EmptyPieChart from './EmptyPieChart';

interface DonutCardProps {
  title: string;
  data: Array<{ name: string; value: number }>;
  colors: string[];
  cardWidth?: number;
  chartHeight?: number;
}

// --- Custom Interactive Legend Component ---

interface CustomLegendProps {
  data: Array<{ name: string; value: number }>;
  colors: string[];
  hiddenSegments: Record<string, boolean>;
  toggleSegment: (name: string) => void;
}

const CustomLegend: React.FC<CustomLegendProps> = ({ data, colors, hiddenSegments, toggleSegment }) => (
  <Stack
    direction="row"
    spacing={2}
    sx={{
      flexWrap: 'wrap',
      justifyContent: 'center',
      rowGap: 1,
    }}
  >
    {data.map((entry, idx) => {
      const isHidden = hiddenSegments[entry.name];
      return (
        <Stack
          key={entry.name}
          direction="row"
          spacing={1}
          alignItems="center"
          onClick={() => toggleSegment(entry.name)} // 🛑 Click handler
          sx={{
            cursor: 'pointer',
            opacity: isHidden ? 0.5 : 1, // Dim if hidden
            userSelect: 'none',
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              backgroundColor: colors[idx],
            }}
          />
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              whiteSpace: 'nowrap',
              lineHeight: 1,
              fontSize: 10,
              p: 0,
              m: 2,
              textDecoration: isHidden ? 'line-through' : 'none',
            }}
          >
            {/* {entry.name.replace(/client/gi, '').replace(/dm/gi, '')} */}
            {entry.name.replace(/^client\s*/i, '').replace(/^dm\s*/i, '')}
          </Typography>
        </Stack>
      );
    })}
  </Stack>
);

const DonutCard = ({ title, data, colors, cardWidth, chartHeight = 180 }: DonutCardProps) => {
  // 🛑 1. State to track hidden segments
  // Key: Segment name, Value: boolean (true if hidden)
  const [hiddenSegments, setHiddenSegments] = useState<Record<string, boolean>>({});

  // 🛑 2. Handler to toggle segment visibility
  const toggleSegment = useCallback((name: string) => {
    setHiddenSegments((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  }, []);

  // 🛑 3. Filter data for rendering
  const visibleData = data.filter((d) => !hiddenSegments[d.name]);

  const visibleColors = colors.filter((_, index) => {
    const name = data[index]?.name;
    return name ? !hiddenSegments[name] : false;
  });

  const total = data?.reduce((acc, d) => acc + d.value, 0);

  // Original center label logic (now removed since labels are per segment)
  // const mainValue = data[0]?.value ?? 0;
  // const percent = total > 0 ? Math.round((mainValue / total) * 100) : 0;

  return (
    <>
      <Typography
        variant="body1"
        sx={{
          fontWeight: 600,
          color: 'text.primary',
        }}
      >
        {title}
      </Typography>
      {!data || total === 0 ? (
        <EmptyPieChart height={chartHeight} data={data} colors={colors} />
      ) : (
        <Stack spacing={1} alignItems="center">
          <ResponsiveContainer width={cardWidth || 540} height={chartHeight}>
            <PieChart>
              <Pie
                dataKey="value"
                isAnimationActive={false}
                data={visibleData} // 🛑 Use visibleData
                innerRadius={45}
                outerRadius={75}
                fill="#8884d8"
                labelLine={false}
                stroke="#fff"
                strokeWidth={0}
                label={({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
                  const visibleTotal = visibleData.reduce((acc, d) => acc + d.value, 0);
                  if (!visibleTotal) return null;
                  const percent = (value / visibleTotal) * 100;
                  if (percent === 0) return null;
                  const RADIAN = Math.PI / 180;
                  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                  const x = cx + radius * Math.cos(-midAngle * RADIAN);
                  const y = cy + radius * Math.sin(-midAngle * RADIAN);
                  return React.createElement(
                    'text',
                    {
                      x,
                      y,
                      fill: '#000',
                      textAnchor: 'middle',
                      dominantBaseline: 'middle',
                      fontSize: 12,
                      fontWeight: '500',
                    },
                    `${Math.round(percent)}%`
                  );
                }}
              >
                {/* We map over the ORIGINAL data to maintain consistent colors/keys, 
                                but only draw a Cell if the segment is NOT hidden. 
                                We must use the original data map index to fetch the color. */}
                {visibleColors.map((entry: string) => (
                  <Cell key={entry} fill={entry} />
                ))}
              </Pie>
              <Tooltip
                wrapperStyle={{ zIndex: 10000 }}
                contentStyle={{ fontSize: 10, padding: '4px 6px', borderRadius: 6 }}
                // Optional: Custom Tooltip formatter to show the actual total value,
                // not just the visible total value percentage.
              />
            </PieChart>
          </ResponsiveContainer>

          {/* 🛑 Use the CustomLegend component */}
          <CustomLegend data={data} colors={colors} hiddenSegments={hiddenSegments} toggleSegment={toggleSegment} />
        </Stack>
      )}
    </>
  );
};

export default DonutCard;
